import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Col, Row } from 'antd';

import { AppDispatch } from 'app/store';

import AttachmentsComponent from 'components/AttachmentsComponent/AttachmentsComponent';

import { selectActiveApplication } from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';
import { updateApplication } from 'features/applications/applicationsSlice';
import { selectActiveJobseeker } from 'features/jobseekers/jobseekersSelectors';
import jobseekersService from 'features/jobseekers/jobseekersService';
import { updateJobseeker } from 'features/jobseekers/jobseekersSlice';
import { selectAppLocale } from 'features/locale/localeSelectors';
import { setSidebarContent } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';

import { Application } from 'types/applications';
import { Jobseeker } from 'types/jobseekers';

import {
  AttachmentsOwnerEntities,
  JobseekerAttachmentTypes,
  SidebarContentTypes
} from 'constants/enums';
import { JOBSEEKER_ATTACHMENTS_FILE_TYPES } from 'constants/jobseekerSettings';
import { SECTION_ROW_GUTTER } from 'constants/uiSettings';

import { ReactComponent as ArrowIcon } from 'assets/icons/arrowDown.svg';

import styles from './AttachmentsView.module.scss';
import { useIsShowAttachments } from './hooks/useIsShowAttachments';

interface AttachmentsViewProps {
  formFieldsValues?: Partial<Jobseeker | Application>;
  attachmentsOwnerEntity: AttachmentsOwnerEntities;
}

const AttachmentsView: FC<AttachmentsViewProps> = ({
  formFieldsValues,
  attachmentsOwnerEntity
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const catchError = useErrorCatch();
  const jobseeker = useSelector(selectActiveJobseeker);
  const application = useSelector(selectActiveApplication);
  const locale = useSelector(selectAppLocale);

  let sideBarContentType: SidebarContentTypes;
  let activeEntityIdId;
  let attachments = [];
  const isSidebarMode = !formFieldsValues;
  let ownerId;
  let childId;

  switch (attachmentsOwnerEntity) {
    case AttachmentsOwnerEntities.Jobseeker:
      sideBarContentType = SidebarContentTypes.JobseekerView;
      activeEntityIdId = jobseeker?.id;
      attachments = jobseeker?.attachments || [];
      ownerId = jobseeker?.id;
      break;

    case AttachmentsOwnerEntities.Application:
      sideBarContentType = SidebarContentTypes.ApplicationView;
      activeEntityIdId = application?.id;
      attachments = application?.attachments || [];
      ownerId = application?.jobseekerId;
      childId = application?.id;
      break;
  }

  const handleBackClick = () => {
    dispatch(
      setSidebarContent({
        contentId: sideBarContentType,
        activeUserId: activeEntityIdId
      })
    );
  };

  const {
    isShowCV,
    isShowPassport,
    isShowIdCard,
    isShowPersonalDocumentsSection,
    isShowVisa,
    isShowWorkPermit,
    isShowOfferLetter,
    isShowImmigrationDocumentsSection,
    isShowDriverLicense,
    isShowOtherJobseekersDocuments
  } = useIsShowAttachments({
    attachments,
    isSidebarMode,
    attachmentsOwnerEntity
  });

  const handleAttachmentChange = () => {
    if (!activeEntityIdId) {
      return;
    }

    switch (attachmentsOwnerEntity) {
      case AttachmentsOwnerEntities.Jobseeker:
        handleUpdateJobseeker(activeEntityIdId);
        break;

      case AttachmentsOwnerEntities.Application:
        handleUpdateApplication(activeEntityIdId);
        break;
    }
  };

  const handleUpdateJobseeker = async (id: string) => {
    try {
      const res = await jobseekersService.getJobseekerById(id);
      let updatedJobseeker = { ...res };

      if (formFieldsValues) {
        updatedJobseeker = {
          ...updatedJobseeker,
          ...(formFieldsValues as Partial<Jobseeker>)
        };
      }

      dispatch(updateJobseeker(updatedJobseeker));
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.fetchJobseeker')
        }
      });
    }
  };

  const handleUpdateApplication = async (id: string) => {
    try {
      const res = await applicationsService.getApplicationById(id, locale);
      dispatch(updateApplication(res));
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.fetchApplication')
        }
      });
    }
  };

  return (
    <div className={styles.container}>
      {isSidebarMode && (
        <Row gutter={SECTION_ROW_GUTTER}>
          <Col span={24}>
            <Button
              icon={<ArrowIcon />}
              onClick={handleBackClick}
              className={styles.backButton}
            >
              {t('form.backCheck.label')}
            </Button>
          </Col>
          <Col span={24}>
            <div className={styles.title}>
              {t('attachment.allFiles', { amount: attachments.length })}
            </div>
          </Col>
        </Row>
      )}

      <Row gutter={SECTION_ROW_GUTTER}>
        <Col span={24}>
          {isShowCV && (
            <div className={styles.fileSection}>
              <div className={styles.subHeader}>{t('attachment.cv')}</div>
              <AttachmentsComponent
                onChange={handleAttachmentChange}
                attachmentType={JobseekerAttachmentTypes.CV}
                ownerId={ownerId}
                childId={childId}
                editable={Boolean(ownerId)}
                attachmentList={attachments}
                direction={isSidebarMode ? 'vertical' : 'horizontal'}
                allowedFileTypes={
                  JOBSEEKER_ATTACHMENTS_FILE_TYPES[JobseekerAttachmentTypes.CV]
                }
                isHideUpload={isSidebarMode}
                isConfirmDelete={isSidebarMode}
                isPreviewFile={isSidebarMode}
              />
            </div>
          )}

          {isShowPersonalDocumentsSection && (
            <div className={styles.fileSection}>
              <div className={styles.subHeader}>{t('attachment.personal')}</div>
              {isShowPassport && (
                <div className={styles.fileSubSection}>
                  <AttachmentsComponent
                    onChange={handleAttachmentChange}
                    attachmentType={JobseekerAttachmentTypes.Passport}
                    ownerId={ownerId}
                    childId={childId}
                    editable={Boolean(ownerId)}
                    attachmentList={attachments}
                    direction={isSidebarMode ? 'vertical' : 'horizontal'}
                    allowedFileTypes={
                      JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                        JobseekerAttachmentTypes.Passport
                      ]
                    }
                    label={t('attachment.passport')}
                    isHideUpload={isSidebarMode}
                    isConfirmDelete={isSidebarMode}
                    isPreviewFile={isSidebarMode}
                  />
                </div>
              )}

              {isShowIdCard && (
                <div className={styles.fileSubSection}>
                  <AttachmentsComponent
                    onChange={handleAttachmentChange}
                    attachmentType={JobseekerAttachmentTypes.IdCard}
                    ownerId={ownerId}
                    childId={childId}
                    editable={Boolean(ownerId)}
                    attachmentList={attachments}
                    direction={isSidebarMode ? 'vertical' : 'horizontal'}
                    allowedFileTypes={
                      JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                        JobseekerAttachmentTypes.IdCard
                      ]
                    }
                    label={t('attachment.id')}
                    isHideUpload={isSidebarMode}
                    isConfirmDelete={isSidebarMode}
                    isPreviewFile={isSidebarMode}
                  />
                </div>
              )}

              {isShowDriverLicense && (
                <div className={styles.fileSubSection}>
                  <AttachmentsComponent
                    onChange={handleAttachmentChange}
                    attachmentType={JobseekerAttachmentTypes.DriverLicense}
                    ownerId={ownerId}
                    childId={childId}
                    editable={Boolean(ownerId)}
                    attachmentList={attachments}
                    direction={isSidebarMode ? 'vertical' : 'horizontal'}
                    allowedFileTypes={
                      JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                        JobseekerAttachmentTypes.DriverLicense
                      ]
                    }
                    label={t('attachment.license')}
                    isHideUpload={isSidebarMode}
                    isConfirmDelete={isSidebarMode}
                    isPreviewFile={isSidebarMode}
                  />
                </div>
              )}

              {isShowOtherJobseekersDocuments && (
                <div className={styles.fileSubSection}>
                  <AttachmentsComponent
                    onChange={handleAttachmentChange}
                    attachmentType={
                      JobseekerAttachmentTypes.OtherJobseekerDocuments
                    }
                    ownerId={ownerId}
                    childId={childId}
                    editable={Boolean(ownerId)}
                    attachmentList={attachments}
                    direction={isSidebarMode ? 'vertical' : 'horizontal'}
                    allowedFileTypes={
                      JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                        JobseekerAttachmentTypes.OtherJobseekerDocuments
                      ]
                    }
                    label={t('attachment.other')}
                    isHideUpload={isSidebarMode}
                    isConfirmDelete={isSidebarMode}
                    isPreviewFile={isSidebarMode}
                  />
                </div>
              )}
            </div>
          )}

          {isShowImmigrationDocumentsSection && (
            <div className={styles.fileSection}>
              <div className={styles.subHeader}>
                {t('attachment.immigration')}
              </div>
              {isShowVisa && (
                <div className={styles.fileSubSection}>
                  <AttachmentsComponent
                    onChange={handleAttachmentChange}
                    attachmentType={JobseekerAttachmentTypes.Visa}
                    ownerId={ownerId}
                    childId={childId}
                    editable={Boolean(ownerId)}
                    attachmentList={attachments}
                    direction={isSidebarMode ? 'vertical' : 'horizontal'}
                    allowedFileTypes={
                      JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                        JobseekerAttachmentTypes.Visa
                      ]
                    }
                    label={t('attachment.visa')}
                    isHideUpload={isSidebarMode}
                    isConfirmDelete={isSidebarMode}
                    isPreviewFile={isSidebarMode}
                  />
                </div>
              )}

              {isShowWorkPermit && (
                <div className={styles.fileSubSection}>
                  <AttachmentsComponent
                    onChange={handleAttachmentChange}
                    attachmentType={JobseekerAttachmentTypes.WorkPermit}
                    ownerId={activeEntityIdId}
                    editable={Boolean(activeEntityIdId)}
                    attachmentList={attachments}
                    direction={isSidebarMode ? 'vertical' : 'horizontal'}
                    allowedFileTypes={
                      JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                        JobseekerAttachmentTypes.WorkPermit
                      ]
                    }
                    label={t('attachment.permit')}
                    isHideUpload={isSidebarMode}
                    isConfirmDelete={isSidebarMode}
                    isPreviewFile={isSidebarMode}
                  />
                </div>
              )}
            </div>
          )}

          {isShowOfferLetter && (
            <div className={styles.fileSection}>
              <div className={styles.subHeader}>
                {t('attachment.offerLetter')}
              </div>

              <AttachmentsComponent
                onChange={handleAttachmentChange}
                attachmentType={JobseekerAttachmentTypes.OfferLetter}
                ownerId={ownerId}
                childId={childId}
                editable={Boolean(ownerId)}
                attachmentList={attachments}
                direction={isSidebarMode ? 'vertical' : 'horizontal'}
                allowedFileTypes={
                  JOBSEEKER_ATTACHMENTS_FILE_TYPES[
                    JobseekerAttachmentTypes.OfferLetter
                  ]
                }
                isHideUpload={isSidebarMode}
                isConfirmDelete={isSidebarMode}
                isPreviewFile={isSidebarMode}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AttachmentsView;
